import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, Lock, Info } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const nav = useNavigate();
  const { items, subtotal, shipping, total, clear } = useCart();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    line1: "", line2: "", city: "", state: "", pincode: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto text-center py-32 px-6">
        <div className="overline">Empty basket</div>
        <h1 className="mt-4 font-serif text-5xl text-[#1B3B36]">No pieces to check out.</h1>
        <Link to="/collections" className="mt-8 inline-flex items-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26]">
          Explore the collection
        </Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    for (const [k, label] of [["name","name"],["email","email"],["phone","phone"],["line1","address"],["city","city"],["state","state"],["pincode","pincode"]]) {
      if (!form[k]) return toast.error(`Please share your ${label}`);
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders/create", {
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, image: i.image,
          price: i.price, quantity: i.quantity, buy_url: i.buy_url, size: i.size || null,
        })),
        customer: { name: form.name, email: form.email, phone: form.phone },
        shipping_address: {
          line1: form.line1, line2: form.line2, city: form.city,
          state: form.state, pincode: form.pincode, country: "India",
        },
        notes: form.notes,
      });

      if (!data.payment_provider_ready) {
        // Razorpay not yet configured — mark as pending, take to confirmation with test-mode banner
        toast.info("Order placed in TEST MODE — Razorpay not yet configured.");
        clear();
        nav(`/order/${data.order_id}`);
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Could not load payment gateway. Check your connection.");
        setLoading(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "Iryaa Homes",
        description: `${items.length} handcrafted piece${items.length > 1 ? "s" : ""}`,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#1B3B36" },
        handler: async (resp) => {
          try {
            await api.post("/orders/verify", {
              order_id: data.order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clear();
            nav(`/order/${data.order_id}`);
          } catch (err) {
            toast.error("Payment recorded, but verification failed. Please contact us.");
          }
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place order right now.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="checkout-page">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-20 pb-10">
        <div className="overline">Checkout</div>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl font-light text-[#1B3B36] leading-[1.02] tracking-tight">
          Almost home.
        </h1>
      </section>

      <form onSubmit={submit} className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          <div>
            <div className="overline">01 · Contact</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
              <F label="Full name"><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="checkout-name" className={inp} /></F>
              <F label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="checkout-phone" className={inp} /></F>
              <F label="Email" span2><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="checkout-email" className={inp} /></F>
            </div>
          </div>
          <div>
            <div className="overline">02 · Shipping address</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
              <F label="Address line 1" span2><input value={form.line1} onChange={(e) => set("line1", e.target.value)} data-testid="checkout-line1" className={inp} /></F>
              <F label="Address line 2 (optional)" span2><input value={form.line2} onChange={(e) => set("line2", e.target.value)} className={inp} /></F>
              <F label="City"><input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="checkout-city" className={inp} /></F>
              <F label="State"><input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="checkout-state" className={inp} /></F>
              <F label="Pincode"><input value={form.pincode} onChange={(e) => set("pincode", e.target.value)} data-testid="checkout-pincode" className={inp} /></F>
            </div>
          </div>
          <div>
            <div className="overline">03 · Notes (optional)</div>
            <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Any special instructions for the artisans?" className={`mt-4 ${inp}`} />
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-5">
          <div className="border border-[#D8D4CC] bg-white p-6 md:p-8 sticky top-28">
            <div className="overline">Your basket</div>
            <div className="mt-5 space-y-4 max-h-72 overflow-y-auto">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3">
                  <img src={i.image} alt="" className="w-14 h-16 object-cover bg-[#EFECE5]" />
                  <div className="flex-1 text-[13px]">
                    <div className="font-serif text-[15px] text-[#1B3B36] leading-tight">{i.name}</div>
                    <div className="text-[#4A4A4A]" data-testid={`checkout-item-meta-${i.key}`}>{i.size ? `Size ${i.size} ft · ` : ""}Qty {i.quantity} · ₹{(i.price * i.quantity).toLocaleString("en-IN")}</div>
                  </div>
                </div>
              ))}
            </div>
            <dl className="mt-6 space-y-2 border-t border-[#D8D4CC] pt-4 text-[13px] text-[#4A4A4A]">
              <div className="flex justify-between"><dt>Subtotal</dt><dd>₹{subtotal.toLocaleString("en-IN")}</dd></div>
              <div className="flex justify-between">
                <dt>Shipping {subtotal >= 1000 ? "" : "(free above ₹1,000)"}</dt>
                <dd>{shipping === 0 ? "Free" : `₹${shipping}`}</dd>
              </div>
              <div className="flex justify-between font-serif text-xl text-[#1B3B36] pt-3 border-t border-[#D8D4CC]">
                <dt>Total</dt><dd>₹{total.toLocaleString("en-IN")}</dd>
              </div>
            </dl>
            <button type="submit" disabled={loading} data-testid="checkout-submit" className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26] disabled:opacity-70">
              {loading ? <><Loader2 className="animate-spin" size={14} /> Processing…</> : <>Pay ₹{total.toLocaleString("en-IN")} <ArrowRight size={14} /></>}
            </button>
            <div className="mt-4 flex items-start gap-2 text-[11px] text-[#84968C]">
              <Lock size={12} className="mt-0.5" />
              <span>Secure payment via Razorpay · UPI · Cards · Netbanking</span>
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
}

const inp = "w-full border-0 border-b border-[#D8D4CC] bg-transparent py-3 text-[15px] focus:border-[#1B3B36] outline-none";
function F({ label, span2, children }) {
  return (
    <label className={`block ${span2 ? "md:col-span-2" : ""}`}>
      <span className="overline">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
